import {useCallback, useMemo, useRef, useState} from 'react'

import {HeroPagination, type HeroPaginationProps} from './HeroPagination/HeroPagination'
import {HeroSequence, type HeroSequenceProps, type HeroPlaybackState} from './HeroSequence/HeroSequence'

import Styles from './Hero.module.css'

export interface HeroProps {
  heroSequence: Omit<
    HeroSequenceProps,
    'currentChapterIndex' | 'onNextChapter' | 'onReady' | 'onPlaybackChange' | 'videoRef' | 'seekRequest'
  >
  heroPagination: Omit<HeroPaginationProps, 'onChange' | 'items' | 'currentChapterIndex' | 'playback'> & {
    items: string[]
  }
}

export const defaultProps: Partial<HeroProps> = {}

export const Hero = (props: HeroProps) => {
  const initializedProps: HeroProps = {...defaultProps, ...props}
  const {heroSequence, heroPagination} = initializedProps

  const [currentChapterIndex, setCurrentChapterIndex] = useState<number>(0)
  const [chapterDurations, setChapterDurations] = useState<number[]>([])
  const videoRef = useRef<HTMLVideoElement>(null)
  const [seekRequest, setSeekRequest] = useState(0)
  const [playbackState, setPlaybackState] = useState<HeroPlaybackState>({
    currentTime: 0,
    isPaused: true,
    isPlaying: false,
    isFinished: false,
  })
  const selectChapter = useCallback((index: number) => {
    setCurrentChapterIndex(index)
    setSeekRequest(request => request + 1)
  }, [])
  const togglePlayback = useCallback(async () => {
    const media = videoRef.current
    if (!media) return
    if (media.paused || media.ended) {
      if (media.ended) {
        media.currentTime = 0
        selectChapter(0)
      }
      try {
        await media.play()
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return
        window.reportError(error)
      }
    } else {
      media.pause()
    }
  }, [selectChapter])
  const playback = useMemo(
    () => ({
      ...playbackState,
      elapsed: playbackState.currentTime - (heroSequence.video?.chapterTimestamps[currentChapterIndex] ?? 0),
      onToggle: togglePlayback,
    }),
    [playbackState, heroSequence.video, currentChapterIndex, togglePlayback],
  )
  const items = useMemo(
    () => heroPagination.items.map((label, index) => ({label, duration: chapterDurations[index] ?? 0})),
    [heroPagination.items, chapterDurations],
  )

  return (
    <div className={Styles['Hero']}>
      <HeroSequence
        {...heroSequence}
        currentChapterIndex={currentChapterIndex}
        onNextChapter={setCurrentChapterIndex}
        onReady={setChapterDurations}
        videoRef={videoRef}
        seekRequest={seekRequest}
        onPlaybackChange={setPlaybackState}
      />
      <HeroPagination
        {...heroPagination}
        currentChapterIndex={currentChapterIndex}
        onChange={selectChapter}
        items={items}
        playback={heroSequence.video ? playback : undefined}
      />
    </div>
  )
}
